import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom' // Import Link for potential navigation
import logo from '../../assets/images/logo.svg'
import { logger } from '../../utils'

// --- Assuming similar atom/molecule components exist ---
// Adjust paths as needed for your project structure
import { Text } from '../atoms' // Assuming Button might be needed later
import { ModalStripe } from '../molecules'
import ContactLinks from '../molecules/ContactLinks/ContactLinks'
// --- End Component Assumptions ---

// --- Main Payment Success Component ---
function PaymentSuccessPage (): ReactElement {
  const [searchParams] = useSearchParams()
  const sessionId = useMemo(() => searchParams.get('session_id'), [searchParams])

  type LookupStatus = 'idle' | 'loading' | 'success' | 'error'
  interface CouponInfo { code: string, discountPercentage: number }
  const [lookupStatus, setLookupStatus] = useState<LookupStatus>('idle')
  const [coupon, setCoupon] = useState<CouponInfo | null>(null)
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'error'>('idle')

  useEffect(() => {
    if (sessionId) {
      // In a real app, you would likely send this sessionId to your backend
      // here to verify the session status and fulfill the order (e.g., flag emails as paid).
      // For now, we just log it for confirmation.
      logger.log(`Payment success page loaded for session ID: ${sessionId}`)
      // Example: verifySessionOnBackend(sessionId);
    } else {
      logger.error('Payment success page loaded without a session ID.')
      // Handle cases where the user might land here without a session ID?
      // Maybe redirect to home or show a generic message.
    }
    // Run only on component mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Empty dependency array means this runs once on mount

  // Fetch applied discount information for the checkout session
  useEffect(() => {
    let isMounted = true
    const controller = new AbortController()

    const fetchDiscount = async () => {
      if (!sessionId) return
      try {
        setLookupStatus('loading')
        const res = await fetch('https://buxj4tmda4.execute-api.us-east-2.amazonaws.com/default/stripeWebHook', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ type: 'CHECKOUT_SESSION_LOOKUP', sessionId }),
          signal: controller.signal
        })
        const data = await res.json()
        if (!isMounted) return
        if (data && data.success) {
          if (data.coupon && typeof data.coupon.code === 'string') {
            setCoupon({ code: data.coupon.code, discountPercentage: Number(data.coupon.discountPercentage) || 0 })
          } else {
            setCoupon(null)
          }
          setLookupStatus('success')
        } else {
          setLookupStatus('error')
        }
      } catch (_err) {
        if (!isMounted) return
        setLookupStatus('error')
      }
    }

    fetchDiscount()

    return () => {
      isMounted = false
      controller.abort()
    }
  }, [sessionId])

  const handleCopyCode = async (code: string) => {
    try {
      if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(code)
      } else {
        const tempInput = document.createElement('textarea')
        tempInput.value = code
        document.body.appendChild(tempInput)
        tempInput.select()
        document.execCommand('copy')
        document.body.removeChild(tempInput)
      }
      setCopyState('copied')
      setTimeout(() => setCopyState('idle'), 1500)
    } catch (_err) {
      setCopyState('error')
      setTimeout(() => setCopyState('idle'), 1500)
    }
  }

  // Add custom styles to override Modal backgrounds and ensure mobile responsiveness
  React.useEffect(() => {
    // Create a style element
    const styleEl = document.createElement('style')
    // Define CSS to override Modal backgrounds and fix mobile constraints
    styleEl.innerHTML = `
            .payment-success-container > div > div > div.diamond-corner > div.inner.bg-black {
                background-color: #1E201F !important;
            }
            .payment-success-container > div {
                background-color: #1E201F !important;
            }
            
            /* Mobile responsive overrides */
            @media (max-width: 640px) {
                .payment-success-container,
                .payment-success-container > div,
                .payment-success-container > div > div,
                .payment-success-container > div > div > div {
                    max-width: 100% !important;
                    width: 100% !important;
                    min-width: 0 !important;
                }
            }
        `
    // Add the style element to the document head
    document.head.appendChild(styleEl)

    // Cleanup function to remove the style element when component unmounts
    return () => {
      // Check if the style element is still a child of document.head before removing
      if (styleEl.parentNode === document.head) {
        document.head.removeChild(styleEl)
      }
    }
  }, [])

  // Inject global styles to force scrolling
  React.useEffect(() => {
    document.documentElement.style.overflow = 'auto'
    document.body.style.overflow = 'auto'
    document.body.style.height = 'auto'
    document.body.style.minHeight = '100vh'
    document.body.style.position = 'relative'

    // Cleanup
    return () => {
      document.documentElement.style.removeProperty('overflow')
      document.body.style.removeProperty('overflow')
      document.body.style.removeProperty('height')
      document.body.style.removeProperty('min-height')
      document.body.style.removeProperty('position')
    }
  }, [])

  return (
    <div style={{
      minHeight: '100vh',
      overflow: 'auto',
      position: 'relative',
      backgroundColor: '#101111'
    }}
    >
      {/* Logo - centered on mobile, positioned top-left on desktop */}
      <header className='flex justify-center pt-4 pb-1 sm:absolute sm:left-6 sm:top-6 sm:pt-0 sm:pb-0 md:left-[40px] md:top-[40px]' aria-hidden='true'>
        <img src={logo} className='w-[80px] sm:w-[120px] md:w-[138px]' alt='logo' />
      </header>
      <div
        className='pt-2 sm:pt-10 px-4 sm:px-6 md:px-8 lg:px-0 payment-success-container'
        style={{
          maxWidth: '680px',
          width: '100%',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'center'
        }}
      >
        <ModalStripe tall>
          <Text
            className='font-bold text-center text-xl sm:text-2xl md:text-3xl text-dopage-darkGreen mb-2 sm:mb-0'
            bold
            size='LG'
            text='Success'
            type='paragraph'
          />
          <div className='mt-8 sm:mt-20 md:mt-40 text-center' />
          <div className='w-full px-2 sm:px-4 md:px-8 min-h-768:mt-10 min-h-1024:mt-14 flex flex-col items-center'> {/* Center content */}
            <Text
              className='font-bold text-dopage-lightGreen text-lg sm:text-xl md:text-2xl text-center leading-snug' // Centered
              bold
              size='LG'
              text='You just purchased something priceless: the power to save a life.'
              type='paragraph'
            />
            <div className='mt-4 sm:mt-6 text-center max-w-md'> {/* Centered text block */}
              <Text
                className='text-dopage-primary text-sm sm:text-base md:text-lg leading-relaxed' // Adjusted line height
                size='SM' // Keep prop if needed by component
                text='Sign-on instructions have been sent directly to the student email(s) you provided. Students should check their inboxes for a message from DopaGE.'
              />
            </div>
            {/* Discount Info block (below sign-on instructions) */}
            {sessionId && (
              <div className='mt-3 sm:mt-4 w-full max-w-md text-left'>
                {lookupStatus === 'loading' && (
                  <div className='text-center text-gray-400 text-xs sm:text-sm'>Retrieving discount...</div>
                )}
                {lookupStatus === 'success' && coupon && (
                  <div data-testid='discount-info' className='p-3 sm:p-4 border border-gray-700 rounded-md bg-[#1E201F] flex flex-col items-center justify-center text-center space-y-1.5 sm:space-y-2'>
                    <div className='text-white text-xs sm:text-sm font-semibold'>Share with friends!</div>
                    <div className='flex items-center gap-2'>
                      <div className='text-dopage-primary text-xs sm:text-sm'>
                        Code: <span className='font-mono'>{coupon.code}</span>
                      </div>
                      <button
                        type='button'
                        aria-label='Copy discount code'
                        data-testid='copy-code-button'
                        onClick={() => handleCopyCode(coupon.code)}
                        className='text-gray-300 hover:text-white'
                      >
                        <svg width='18' height='18' viewBox='0 0 24 24' fill='currentColor' aria-hidden='true'>
                          <path d='M16 1H4a2 2 0 0 0-2 2v12h2V3h12V1zm3 4H8a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2zm0 16H8V7h11v14z'/>
                        </svg>
                      </button>
                    </div>
                    <div className='text-white text-xs sm:text-sm'>${((coupon.discountPercentage / 100) * 25).toFixed(2)} off each student</div>
                    <div aria-live='polite' className='text-xs h-4'>
                      {copyState === 'copied' ? 'Copied!' : copyState === 'error' ? 'Copy failed' : ''}
                    </div>
                  </div>
                )}
                {lookupStatus === 'error' && (
                  <div className='text-center text-gray-500 text-xs'>Discount details unavailable.</div>
                )}
              </div>
            )}
            <div className='mt-auto absolute bottom-0 pb-4 sm:pb-8 w-full max-w-sm '> {/* Adjust max-width as needed */}
              <ContactLinks
                id='link-contact-success'
              />
            </div>
          </div>
        </ModalStripe>
      </div>
    </div>
  )
}

export default PaymentSuccessPage
